import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  AlertCircle,
  CreditCard,
  Building,
  Users,
  CheckSquare,
  Square,
  X,
  Home,
  ChevronRight,
  FileText
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Account } from '../../types/account';
import { Client } from '../../types/client';
import { User } from '../../types/auth';
import { PaginatedResponse } from '../../types/common';
import { accountService, userService } from '../../services';
import { useAuth } from '../../hooks/useAuth';
import { AccountMatchesModal } from './AccountMatchesModal';
import { ClientDetailsModal } from './ClientDetailsModal';
import { AccountEmployeesModal } from './AccountEmployeesModal';
import { AccountBulkAssignModal } from './AccountBulkAssignModal';

interface AccountListProps {
  refreshTrigger?: number;
  onCreateAccount: () => void;
  onEditAccount: (account: Account) => void;
}

export const AccountList: React.FC<AccountListProps> = ({
  refreshTrigger = 0,
  onCreateAccount,
  onEditAccount
}) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // R15 Task B2: honour `?search=<term>` in the URL so deep-links from
  // the blotter review modal (ExistingAccountReviewRow) land pre-filtered
  // to the account the user was inspecting. Same lazy-initializer pattern
  // used elsewhere in this component to avoid the render-race documented
  // in MEMORY.md.
  const [searchTerm, setSearchTerm] = useState<string>(
    () => searchParams.get('search') || ''
  );
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [unassignedFilter, setUnassignedFilter] = useState<boolean>(() => {
    return searchParams.get('unassigned') === '1';
  });
  const [noEmployeesFilter, setNoEmployeesFilter] = useState<boolean>(() => {
    return searchParams.get('no_employees') === '1';
  });
  // Initialize filters from URL params immediately to prevent a race condition
  // where the first loadAccounts() fires before the params useEffect sets the filters
  const [clientFilter, setClientFilter] = useState<number | 'all'>(() => {
    const id = searchParams.get('client_id');
    return id ? parseInt(id) : 'all';
  });
  const [userFilter, setUserFilter] = useState<number | 'all'>(() => {
    const id = searchParams.get('user_id');
    return id ? parseInt(id) : 'all';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(25);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter context state
  const [filterClient, setFilterClient] = useState<Client | null>(null);
  const [filterUser, setFilterUser] = useState<User | null>(null);

  // Modal state
  const [selectedAccountForMatches, setSelectedAccountForMatches] = useState<Account | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedAccountForEmployees, setSelectedAccountForEmployees] = useState<Account | null>(null);

  // Bulk assign state
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

  // Statistics state
  const [stats, setStats] = useState({
    totalAccounts: 0,
    activeAccounts: 0,
    totalEmployees: 0,
    avgEmployeesPerAccount: 0
  });

  const loadAccounts = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(clientFilter !== 'all' && { client_id: clientFilter }),
        ...(userFilter !== 'all' && { user_id: userFilter }),
        ...(unassignedFilter && { unassigned: '1' }),
        ...(noEmployeesFilter && { no_employees: '1' })
      };

      const response: PaginatedResponse<Account> = await accountService.getAccounts(params);
      setAccounts(response.data);
      setTotalPages(response.meta.last_page);
      setCurrentPage(response.meta.current_page);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await accountService.getAvailableClients();
      if (response.data) {
        setClients(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load clients:', err);
    }
  };

  // Load display names for breadcrumb based on URL params (filters already set in state initializers)
  useEffect(() => {
    const clientId = searchParams.get('client_id');
    const userId = searchParams.get('user_id');

    if (clientId) {
      accountService.getAvailableClients().then(response => {
        const client = response.data?.find((c: Client) => c.id === parseInt(clientId));
        if (client) setFilterClient(client);
      });
    }

    if (userId) {
      userService.getUsers({ per_page: 1000 }).then(response => {
        const user = response.data?.find((u: User) => u.id === parseInt(userId));
        if (user) setFilterUser(user);
      });
    }
  }, []);

  // Fetch stats for all accounts (separate from pagination)
  const fetchStats = async () => {
    try {
      // Fetch with high per_page to get accurate aggregate counts
      const statsParams = {
        per_page: 10000, // High number to get all records with counts
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(clientFilter !== 'all' && { client_id: clientFilter }),
        ...(userFilter !== 'all' && { user_id: userFilter }),
        ...(unassignedFilter && { unassigned: '1' }),
        ...(noEmployeesFilter && { no_employees: '1' })
      };

      const statsResponse: PaginatedResponse<Account> = await accountService.getAccounts(statsParams);
      const allAccounts = statsResponse.data;
      const total = statsResponse.meta.total;

      // Calculate aggregate stats from the returned data
      const activeCount = allAccounts.filter(acc => acc.is_active).length;
      const totalEmployees = allAccounts.reduce((sum, acc) => sum + (acc.user_accounts?.length || 0), 0);

      setStats({
        totalAccounts: total,
        activeAccounts: activeCount,
        totalEmployees: totalEmployees,
        avgEmployeesPerAccount: total > 0
          ? Math.round((totalEmployees / total) * 10) / 10 // Round to 1 decimal
          : 0
      });
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch accounts when page changes
  useEffect(() => {
    loadAccounts(currentPage);
  }, [currentPage, searchTerm, statusFilter, clientFilter, userFilter, unassignedFilter, noEmployeesFilter, refreshTrigger]);

  // Fetch stats only when filters change (not on page change)
  useEffect(() => {
    fetchStats();
  }, [searchTerm, statusFilter, clientFilter, userFilter, unassignedFilter, noEmployeesFilter, refreshTrigger]);

  useEffect(() => {
    loadClients();
  }, []);

  const clearFilters = () => {
    // Reset EVERY filter — search, status, client, user, unassigned,
    // no-employees — plus paging. Prior version left search/status/page
    // intact, which confused users who clicked "Clear" expecting a
    // full reset.
    setSearchTerm('');
    setStatusFilter('all');
    setClientFilter('all');
    setUserFilter('all');
    setUnassignedFilter(false);
    setNoEmployeesFilter(false);
    setFilterClient(null);
    setFilterUser(null);
    setCurrentPage(1);
    setSearchParams({});
  };

  const handleToggleStatus = async (account: Account) => {
    if (actionLoading === account.id) return;

    try {
      setActionLoading(account.id);
      await accountService.toggleAccountStatus(account.id);
      setSuccessMessage(`Account ${account.is_active ? 'deactivated' : 'activated'} successfully`);
      await loadAccounts(currentPage);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update account status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (account: Account): Promise<void> => {
    const employeeCount = account.user_accounts?.length || 0;
    const hasEmployees = employeeCount > 0;
    let force = false;

    if (hasEmployees) {
      const forceConfirm = window.confirm(
        `"${account.account_name}" has ${employeeCount} assigned employee(s).\n\n` +
        `Do you want to unassign all employees and delete this account?`
      );
      if (!forceConfirm) return;
      force = true;
    } else {
      if (!window.confirm(`Are you sure you want to delete "${account.account_name}"?`)) {
        return;
      }
    }

    try {
      setActionLoading(account.id);
      await accountService.deleteAccount(account.id, force);
      setSuccessMessage('Account deleted successfully');
      await loadAccounts(currentPage);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete account');
      setActionLoading(null);
    }
  };

  const canManageAccount = () => {
    return currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  };

  // Selection handlers
  const toggleAccountSelection = (accountId: number) => {
    const newSelection = new Set(selectedAccounts);
    if (newSelection.has(accountId)) {
      newSelection.delete(accountId);
    } else {
      newSelection.add(accountId);
    }
    setSelectedAccounts(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedAccounts.size === accounts.length) {
      setSelectedAccounts(new Set());
    } else {
      setSelectedAccounts(new Set(accounts.map(acc => acc.id)));
    }
  };

  const getSelectedAccountObjects = (): Account[] => {
    return accounts.filter(acc => selectedAccounts.has(acc.id));
  };

  const handleBulkAssignSuccess = () => {
    setSuccessMessage('Employees assigned successfully');
    setSelectedAccounts(new Set());
    setShowBulkAssignModal(false);
    loadAccounts(currentPage);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CreditCard className="h-8 w-8 text-primary-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Account Management</h2>
            <p className="text-gray-600 mt-1">
              Manage trading accounts and client assignments
            </p>
          </div>
        </div>

        {canManageAccount() && (
          <div className="flex items-center space-x-3">
            {selectedAccounts.size > 0 && (
              <button
                onClick={() => setShowBulkAssignModal(true)}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <Users className="w-4 h-4" />
                <span>Assign Employees ({selectedAccounts.size})</span>
              </button>
            )}
            <button
              onClick={onCreateAccount}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>New Account</span>
            </button>
          </div>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
          <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Breadcrumbs / Filter Banner */}
      {(filterClient || filterUser) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm">
              <Home className="h-4 w-4 text-blue-600" />
              <ChevronRight className="h-4 w-4 text-blue-400" />
              {filterClient && (
                <>
                  <Building className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    Client: {filterClient.client_name}
                  </span>
                  <span className="text-blue-600">
                    ({accounts.length > 0 ? `${accounts.length} accounts` : 'No accounts'})
                  </span>
                </>
              )}
              {filterUser && (
                <>
                  <Users className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    User: {filterUser.name}
                  </span>
                  <span className="text-blue-600">
                    ({accounts.length > 0 ? `${accounts.length} accounts` : 'No accounts'})
                  </span>
                </>
              )}
            </div>
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
            >
              <X className="h-4 w-4" />
              <span className="text-sm font-medium">Clear Filter</span>
            </button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && stats.totalAccounts > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">
                  {filterUser ? `${filterUser.name}'s Accounts` : 'Total Accounts'}
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {stats.totalAccounts}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {filterUser ? 'Accounts assigned to user' : 'Across all pages'}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-600 opacity-75" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Active Accounts</p>
                <p className="text-2xl font-bold text-green-900">
                  {stats.activeAccounts}
                </p>
                <p className="text-xs text-green-600 mt-1">Currently active</p>
              </div>
              <CheckSquare className="h-8 w-8 text-green-600 opacity-75" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total Employees</p>
                <p className="text-2xl font-bold text-purple-900">
                  {stats.totalEmployees}
                </p>
                <p className="text-xs text-purple-600 mt-1">Assigned to accounts</p>
              </div>
              <Users className="h-8 w-8 text-purple-600 opacity-75" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Avg per Account</p>
                <p className="text-2xl font-bold text-orange-900">
                  {stats.avgEmployeesPerAccount}
                </p>
                <p className="text-xs text-orange-600 mt-1">Average employees</p>
              </div>
              <Filter className="h-8 w-8 text-orange-600 opacity-75" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search accounts by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Client Filter */}
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.client_name}
                </option>
              ))}
            </select>
          </div>

          {/* Unassigned / No-Employees Filter Toggles */}
          <div className="flex items-center gap-2 flex-wrap md:col-span-3">
            <button
              onClick={() => setUnassignedFilter(!unassignedFilter)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                unassignedFilter
                  ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <AlertCircle className={`h-4 w-4 ${unassignedFilter ? 'text-amber-600' : 'text-gray-400'}`} />
              {unassignedFilter ? 'Showing Unassigned Only' : 'Show Unassigned Accounts'}
            </button>

            <button
              onClick={() => setNoEmployeesFilter(!noEmployeesFilter)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                noEmployeesFilter
                  ? 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title="Show accounts that have no active employees assigned"
            >
              <Users className={`h-4 w-4 ${noEmployeesFilter ? 'text-orange-600' : 'text-gray-400'}`} />
              {noEmployeesFilter ? 'Showing No-Employees Only' : 'Show No-Employees Accounts'}
            </button>
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      <div className="card overflow-hidden">
        {accounts.length === 0 && !loading ? (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || clientFilter !== 'all' ? 'No accounts found' : 'No accounts yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || clientFilter !== 'all'
                ? 'Try adjusting your search criteria'
                : 'Create your first account to get started'
              }
            </p>
            {!searchTerm && clientFilter === 'all' && canManageAccount() && (
              <button
                onClick={onCreateAccount}
                className="btn btn-primary"
              >
                Create First Account
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                  {canManageAccount() && (
                    <th className="px-4 py-3 text-center">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-500 hover:text-gray-700"
                        title={selectedAccounts.size === accounts.length ? 'Deselect All' : 'Select All'}
                      >
                        {selectedAccounts.size === accounts.length ? (
                          <CheckSquare className="h-5 w-5" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employees
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Settings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    {canManageAccount() && (
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => toggleAccountSelection(account.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {selectedAccounts.has(account.id) ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {account.account_name}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Created {new Date(account.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => account.client && setSelectedClient(account.client)}
                        className="flex items-center space-x-2 text-left hover:bg-blue-50 p-2 rounded transition-colors w-full"
                        disabled={!account.client}
                      >
                        <Building className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                            {account.client?.client_name || 'Unknown Client'}
                          </div>
                          <div className={`text-xs ${account.client?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                            {account.client?.is_active ? 'Active' : 'Inactive'} Client
                          </div>
                        </div>
                      </button>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedAccountForEmployees(account)}
                        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                        title="View assigned employees"
                      >
                        <Users className="h-4 w-4" />
                        <div className="text-left">
                          <div>{account.user_accounts?.length || 0} assigned</div>
                          {account.total_commission_percentage && (
                            <div className="text-xs text-gray-500">
                              {account.total_commission_percentage.toFixed(2)}% total
                            </div>
                          )}
                        </div>
                      </button>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedAccountForMatches(account)}
                        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                        title="View match strings"
                      >
                        <FileText className="h-4 w-4" />
                        <span>{account.account_matches_count || 0}</span>
                      </button>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {account.auto_assign ? (
                          <CheckSquare className="h-4 w-4 text-green-600" />
                        ) : (
                          <Square className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-600">
                          {account.auto_assign ? 'Auto-assign' : 'Manual'}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {canManageAccount() ? (
                        <button
                          onClick={() => handleToggleStatus(account)}
                          disabled={actionLoading === account.id}
                          className={`
                            flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                            ${account.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }
                          `}
                        >
                          {actionLoading === account.id ? (
                            <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                          ) : account.is_active ? (
                            <>
                              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                              <span>Active</span>
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                              <span>Inactive</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                          ${account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        `}>
                          <div className={`w-2 h-2 rounded-full ${account.is_active ? 'bg-green-600' : 'bg-red-600'}`}></div>
                          <span>{account.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {canManageAccount() && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onEditAccount(account)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit Account"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => handleDelete(account)}
                            disabled={actionLoading === account.id}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Account"
                          >
                            {actionLoading === account.id ? (
                              <div className="animate-spin h-4 w-4 border border-current border-t-transparent rounded-full"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {accounts.map((account) => (
                <div key={account.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900">{account.account_name}</h3>
                      <p className="text-xs text-gray-400 mt-1">
                        Created {new Date(account.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      {canManageAccount() ? (
                        <button
                          onClick={() => handleToggleStatus(account)}
                          disabled={actionLoading === account.id}
                          className={`
                            flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                            ${account.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }
                          `}
                        >
                          {actionLoading === account.id ? (
                            <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                          ) : (
                            <>
                              <div className={`w-2 h-2 rounded-full ${account.is_active ? 'bg-green-600' : 'bg-red-600'}`}></div>
                              <span>{account.is_active ? 'Active' : 'Inactive'}</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                          ${account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        `}>
                          <div className={`w-2 h-2 rounded-full ${account.is_active ? 'bg-green-600' : 'bg-red-600'}`}></div>
                          <span>{account.is_active ? 'Active' : 'Inactive'}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Client and Employee Info */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Building className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-600">Client</span>
                      </div>
                      <button
                        onClick={() => account.client && setSelectedClient(account.client)}
                        className="text-left w-full hover:bg-blue-50 p-1 rounded transition-colors"
                        disabled={!account.client}
                      >
                        <div className="font-medium text-blue-600 hover:text-blue-800">
                          {account.client?.client_name || 'Unknown Client'}
                        </div>
                        <div className={`${account.client?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                          {account.client?.is_active ? 'Active' : 'Inactive'} Client
                        </div>
                      </button>
                    </div>

                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Users className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-600">Employees</span>
                      </div>
                      <button
                        onClick={() => setSelectedAccountForEmployees(account)}
                        className="text-left w-full hover:bg-blue-50 p-1 rounded transition-colors"
                      >
                        <div className="font-medium text-blue-600 hover:text-blue-800">
                          {account.user_accounts?.length || 0} assigned
                        </div>
                        {account.total_commission_percentage && (
                          <div className="text-gray-500">
                            {account.total_commission_percentage.toFixed(2)}% total
                          </div>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="flex items-center text-xs text-gray-600">
                    {account.auto_assign ? (
                      <CheckSquare className="h-3 w-3 text-green-600 mr-1" />
                    ) : (
                      <Square className="h-3 w-3 text-gray-400 mr-1" />
                    )}
                    <span>{account.auto_assign ? 'Auto-assign enabled' : 'Manual assignment'}</span>
                  </div>

                  {/* Actions */}
                  {canManageAccount() && (
                    <div className="flex items-center space-x-3 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => onEditAccount(account)}
                        className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-900 text-xs"
                      >
                        <Edit className="h-3 w-3" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(account)}
                        disabled={actionLoading === account.id}
                        className="flex items-center space-x-1 text-red-600 hover:text-red-900 text-xs"
                      >
                        {actionLoading === account.id ? (
                          <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Account Matches Modal */}
      {selectedAccountForMatches && (
        <AccountMatchesModal
          accountId={selectedAccountForMatches.id}
          accountName={selectedAccountForMatches.account_name}
          onClose={() => setSelectedAccountForMatches(null)}
        />
      )}

      {/* Client Details Modal */}
      {selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          accountCount={accounts.filter(a => a.client_id === selectedClient.id).length}
          onClose={() => setSelectedClient(null)}
        />
      )}

      {/* Account Employees Modal */}
      {selectedAccountForEmployees && (
        <AccountEmployeesModal
          account={selectedAccountForEmployees}
          onClose={() => setSelectedAccountForEmployees(null)}
          onUpdate={() => loadAccounts(currentPage)}
        />
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && selectedAccounts.size > 0 && (
        <AccountBulkAssignModal
          accounts={getSelectedAccountObjects()}
          onClose={() => setShowBulkAssignModal(false)}
          onSuccess={handleBulkAssignSuccess}
        />
      )}
    </div>
  );
};